import React, { Fragment, useEffect, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import "./style.scss";
import { textIdentifier } from "../../../textIdentifier";
import {
  resetCompetition,
  resetCompetitionReschedule,
} from "../../../store/actions";

const convertUTCToLocal = (utcDateTime) => {
  if (!utcDateTime) return { localDate: "", localTime: "" };
  const localDateObj = new Date(utcDateTime);
  const localDate = localDateObj.toISOString().split("T")[0];
  const localTime = localDateObj.toTimeString().slice(0, 5);
  return { localDate, localTime };
};

const ResetSection = ({
  handleClose,
  lang,
  selectedCompetition,
  selectedClass,
  student,
  setStudents,
}) => {
  const dispatch = useDispatch();
  const {
    resetCompetitionLoader,
    resetCompetitionSuccess,
    resetCompetitionInfo,
  } = useSelector((state) => state.main);
  const handleResetClick = () => {
    dispatch(
      resetCompetition(
        selectedCompetition.id,
        selectedClass.code,
        student.id,
        null,
        null
      )
    );
  };

  useEffect(() => {
    if (!resetCompetitionLoader && resetCompetitionSuccess) {
      setStudents((prev) =>
        prev.map((i) =>
          i.id === student.id
            ? {
                ...i,
                competition_status: resetCompetitionInfo.competition_status,
                reschedule: { ...resetCompetitionInfo.reschedule },
              }
            : { ...i }
        )
      );
      dispatch(resetCompetitionReschedule());
      handleClose();
    }
  }, [resetCompetitionLoader, resetCompetitionSuccess]);

  return (
    <div className="footer-btn">
      <button className="cancel-btn" onClick={handleClose}>
        {textIdentifier["cancel"][lang]}
      </button>
      <button
        className={[
          "reset-btn",
          resetCompetitionLoader && "reset-btn--disabled",
        ].join(" ")}
        onClick={handleResetClick}
      >
        {textIdentifier["reset-now"][lang]}
      </button>
    </div>
  );
};

const RescheduleSection = ({
  handleClose,
  lang,
  selectedCompetition,
  selectedClass,
  student,
  setStudents,
}) => {
  const dispatch = useDispatch();
  const {
    resetCompetitionLoader,
    resetCompetitionSuccess,
    resetCompetitionInfo,
  } = useSelector((state) => state.main);
  const reschedule = student.reschedule || {};
  const { localDate: initialStartDate, localTime: initialStartTime } =
    convertUTCToLocal(reschedule.start_time);
  const { localDate: initialEndDate, localTime: initialEndTime } =
    convertUTCToLocal(reschedule.end_time);

  const [startdate, setStartdate] = useState({ value: initialStartDate });
  const [starttime, setStarttime] = useState({ value: initialStartTime });
  const [enddate, setEnddate] = useState({ value: initialEndDate });
  const [endtime, setEndtime] = useState({ value: initialEndTime });
  const [isResetDisabled, setIsResetDisabled] = useState(false);
  const endDateRef = useRef();
  const endTimeRef = useRef();
  const startDateRef = useRef();
  const startTimeRef = useRef();
  const [isEndDateError,setEndDateError] = useState(false);
  const currentDate = new Date().toISOString().split("T")[0];

  const combineDateTime = (date, time) => {
    return new Date(`${date}T${time}`);
  };

  const handleResetClick = () => {
    if (startdate.value && starttime.value && enddate.value && endtime.value) {
      const startDateTimeUTC = combineDateTime(
        startdate.value,
        starttime.value
      ).toISOString();
      const endDateTimeUTC = combineDateTime(
        enddate.value,
        endtime.value
      ).toISOString();
      dispatch(
        resetCompetition(
          selectedCompetition.id,
          selectedClass.code,
          student.id,
          startDateTimeUTC,
          endDateTimeUTC
        )
      );
    } else {
      dispatch(
        resetCompetition(
          selectedCompetition.id,
          selectedClass.code,
          student.id,
          null,
          null
        )
      );
    }
  };

  const handleStartDateChange = (e) => {
    setStartdate({ value: e.target.value });
  };

  const handleStartTimeChange = (e) => {
    setStarttime({ value: e.target.value });
  };

  const handleEndDateChange = (e) => {
    setEnddate({ value: e.target.value });
  };

  const handleEndTimeChange = (e) => {
    setEndtime({ value: e.target.value });
  };

  const checkIfResetEnabled = () => {
    if (startdate.value && starttime.value && enddate.value && endtime.value) {
      const startDateTime = combineDateTime(startdate.value, starttime.value);
      const endDateTime = combineDateTime(enddate.value, endtime.value);
      return endDateTime > startDateTime;
    }
    return false;
  };

  const checkEndDateError = () => {
    if (startdate.value && starttime.value && enddate.value && endtime.value) {
      const startDateTime = combineDateTime(startdate.value, starttime.value);
      const endDateTime = combineDateTime(enddate.value, endtime.value);
      return endDateTime <= startDateTime;
    }
    return false;
  }

  useEffect(() => {
    setIsResetDisabled(!checkIfResetEnabled());
    setEndDateError(checkEndDateError());
  }, [startdate, starttime, enddate, endtime]);

  useEffect(() => {
    if (!resetCompetitionLoader && resetCompetitionSuccess) {
      setStudents((prev) =>
        prev.map((i) =>
          i.id === student.id
            ? {
                ...i,
                competition_status: resetCompetitionInfo.competition_status,
                reschedule: { ...resetCompetitionInfo.reschedule },
              }
            : { ...i }
        )
      );
      dispatch(resetCompetitionReschedule());
      handleClose();
    }
  }, [resetCompetitionLoader, resetCompetitionSuccess]);

  return (
    <div className="reschedule-cnt">
      <div className="date-field">
        <div className="header-label">{textIdentifier["start-date"][lang]}</div>
        <div className="inp-field-cnt">
          <input
            className="inp-field"
            ref={startDateRef}
            tabIndex={-1}
            onClick={(e) => {
              e.target.showPicker();
            }}
            value={startdate.value}
            onChange={(e) => {
              handleStartDateChange(e);
            }}
            type="date"
            lang="hi"
            min={currentDate}
          />
          <input
            className="inp-field"
            ref={startTimeRef}
            tabIndex={-1}
            onClick={(e) => {
              e.target.showPicker();
            }}
            value={starttime.value}
            onChange={(e) => {
              handleStartTimeChange(e);
            }}
            type="time"
            lang="hi"
          />
        </div>
      </div>
      <div className="date-field">
        <div className="header-label">{textIdentifier["end-date"][lang]}</div>
        <div className="inp-field-cnt">
          <input
            className={["inp-field",isEndDateError && "inp-field--error"].join(" ")}
            ref={endDateRef}
            tabIndex={-1}
            onClick={(e) => {
              e.target.showPicker();
            }}
            value={enddate.value}
            onChange={(e) => {
              handleEndDateChange(e);
            }}
            type="date"
            lang="hi"
            min={currentDate}
          />
          <input
            className={["inp-field",isEndDateError && "inp-field--error"].join(" ")}
            ref={endTimeRef}
            tabIndex={-1}
            onClick={(e) => {
              e.target.showPicker();
            }}
            value={endtime.value}
            onChange={(e) => {
              handleEndTimeChange(e);
            }}
            type="time"
            lang="hi"
          />
        </div>
      </div>
      <div className="note">
        {`${textIdentifier["note"][lang]}: ${textIdentifier["clicking-on-reschedule-will-reset-the-progress-of-this-student"][lang]}.`}
      </div>
      <div className="footer-btn">
        <button className="cancel-btn" onClick={handleClose}>
          {textIdentifier["cancel"][lang]}
        </button>
        <button
          className={[
            "reset-btn",
            resetCompetitionLoader && "reset-btn--disabled",
            isResetDisabled && "reset-btn--disabled",
          ].join(" ")}
          onClick={handleResetClick}
        >
          {textIdentifier["reschedule"][lang]}
        </button>
      </div>
    </div>
  );
};

const CompetitonResetModal = ({ student, handleClose, setStudents }) => {
  const { selectedCompetition, selectedClass, lang } = useSelector(
    (state) => state.main
  );
  const [tabs, setTabs] = useState([
    {
      id: 0,
      tabName: "Reset",
      isActive: true,
    },
    {
      id: 1,
      tabName: "Reschedule",
      isActive: false,
    },
  ]);
  const [currentTab, setCurrentTab] = useState(0);

  const handleTabClick = (id) => {
    setTabs((prev) =>
      prev.map((i) =>
        i.id === id ? { ...i, isActive: true } : { ...i, isActive: false }
      )
    );
    setCurrentTab(id);
  };

  return (
    <div className="reset-modal-cnt">
      <div className="reset-modal-body">
        <div className="header">
          <div className="header--text">
            {textIdentifier["change-deadline"][lang]}
          </div>
          <div className="cross-cnt" onClick={handleClose}>
            <img src="/static/images/svg/cross-icon.svg" alt="cross icon" />
          </div>
        </div>
        <div className={"tabnav-cnt"}>
          {tabs.map((i, index) => (
            <div
              className={["tab-cnt", i.isActive && "tab-cnt--active"].join(" ")}
              key={i.id}
              onClick={() => handleTabClick(i.id)}
            >
              <div
                className={[
                  "indicator",
                  i.isActive && "indicator--active",
                ].join(" ")}
              >
                {i.isActive && <div className="inner"></div>}
              </div>
              {i.tabName}
            </div>
          ))}
        </div>
        <div className="student-name">
          {`"${student.first_name} ${student.last_name}"`}
        </div>
        <div className={"reset-info"}>
          The progress of this student in both competition and practice will be
          reset, allowing the student to reattempt them.
        </div>
        {currentTab == 0 ? (
          <ResetSection
            student={student}
            handleClose={handleClose}
            lang={lang}
            selectedCompetition={selectedCompetition}
            selectedClass={selectedClass}
            setStudents={setStudents}
          />
        ) : (
          <RescheduleSection
            student={student}
            handleClose={handleClose}
            lang={lang}
            selectedCompetition={selectedCompetition}
            selectedClass={selectedClass}
            setStudents={setStudents}
          />
        )}
      </div>
    </div>
  );
};

export default CompetitonResetModal;
